import React from 'react';
import { Box, Button, SpaceBetween } from '@amzn/awsui-components-core';
import { TICKET_LINKS } from '../../constants/appConstants';
import noAccessImage from '../../images/no-access-to-verity-app.png';

/**
 * Props for NoAccessToVerity component
 *
 * All props are optional — the component renders sensible defaults
 * for the app-level "no access" scenario when no props are provided.
 */
interface NoAccessToVerityProps {
  /** Heading text displayed prominently. Defaults to "You do not have permission to access verity" */
  heading?: string;
  /** Description text below the heading. Defaults to a generic permission message */
  description?: string;
  /**
   * Image source to display above the heading.
   * Pass `null` to hide the image entirely.
   * Defaults to the lock icon image.
   */
  imageSrc?: string | null;
  /** Alt text for the image. Defaults to "Access restricted" */
  imageAlt?: string;
  /** Label for the primary action button. Defaults to "Request Access" */
  buttonLabel?: string;
  /** Handler for the primary action button. Defaults to opening the Verity support ticket link */
  onButtonClick?: () => void;
  /** Whether to show an external icon on the button. Defaults to true */
  buttonExternal?: boolean;
}

/**
 * NoAccessToVerity component
 *
 * Displays when a user doesn't have permission to access the Verity application
 * or a specific resource. Supports customization via optional props.
 *
 * Shows:
 * - Optional image (lock icon by default, or hidden via `imageSrc={null}`)
 * - Heading message
 * - Description text
 * - Primary action button
 *
 * @example Default usage (app-level no access)
 * ```tsx
 * <NoAccessToVerity />
 * ```
 *
 * @example Custom message with no image
 * ```tsx
 * <NoAccessToVerity
 *   heading="Report not found"
 *   description="The report you are looking for does not exist."
 *   imageSrc={null}
 *   buttonLabel="Go to Home"
 *   onButtonClick={() => navigate('/')}
 *   buttonExternal={false}
 * />
 * ```
 */
export const NoAccessToVerity: React.FC<NoAccessToVerityProps> = ({
  heading = 'You do not have permission to access verity',
  description = 'We\u2019re sorry, your account does not have permission to access this page.',
  imageSrc = noAccessImage,
  imageAlt = 'Access restricted',
  buttonLabel = 'Request Access',
  onButtonClick,
  buttonExternal = true,
}) => {
  const handleDefaultClick = () => {
    window.open(TICKET_LINKS.VERITY_SUPPORT.href, '_blank', 'noopener,noreferrer');
  };

  return (
    <Box textAlign="center" padding={{ vertical: 'xxxl', horizontal: 'xl' }} data-testid="access-restricted-page">
      <SpaceBetween size="l">
        {/* Optional image — hidden when imageSrc is null */}
        {imageSrc !== null && (
          <Box>
            <img
              src={imageSrc}
              alt={imageAlt}
              style={{
                maxWidth: '300px',
                width: '100%',
                height: 'auto',
              }}
            />
          </Box>
        )}

        {/* Main heading */}
        <Box variant="h1" fontWeight="bold">
          {heading}
        </Box>

        {/* Description text */}
        <Box variant="p" color="text-body-secondary" fontSize="body-m">
          {description}
        </Box>

        {/* Primary action button */}
        <Box textAlign="center">
          <div style={{ display: 'inline-block' }}>
            <Button
              variant="primary"
              onClick={onButtonClick ?? handleDefaultClick}
              {...(buttonExternal ? { iconAlign: 'right' as const, iconName: 'external' as const } : {})}
            >
              {buttonLabel}
            </Button>
          </div>
        </Box>
      </SpaceBetween>
    </Box>
  );
};

export default NoAccessToVerity;
